import React, { useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { Toast } from 'primereact/toast'
import { RejectedItemsAnalysisView } from './RejectedItemsAnalysisView'
import { ReassignmentConfirmationView } from './ReassignmentConfirmationView'
import { RejectedItemsResultsView } from './RejectedItemsResultsView'
import { useOrderData } from '../../../hooks/useOrderData'
import { useLineItems } from '../../../hooks/useLineItems'
import { useOrderSelection } from '../../../hooks/useOrderSelection'
import { useLineItemSelection } from '../../../hooks/useLineItemSelection'
import { useBulkRejectedItemsReassignment } from '../../../hooks/useBulkRejectedItemsReassignment'

type DialogStep = 'analysis' | 'confirmation' | 'results'
type ProcessingState = 'idle' | 'processing' | 'completed' | 'error'

interface OrderAnalysis {
  storeDeclinedOnly: number
  adminRejectedOnly: number
  mixedOrders: number
  totalOrders: number
}

interface BulkRejectedItemsDialogProps {
  orderData: ReturnType<typeof useOrderData>
  orderSelection: ReturnType<typeof useOrderSelection>
  lineItemsHook: ReturnType<typeof useLineItems>
  lineItemSelection: ReturnType<typeof useLineItemSelection>
  visible: boolean
  onCloseDialog: () => void
}

export const BulkRejectedItemsDialog: React.FC<BulkRejectedItemsDialogProps> = ({
  orderData,
  orderSelection,
  lineItemsHook,
  lineItemSelection,
  visible,
  onCloseDialog,
}) => {
  const [currentStep, setCurrentStep] = useState<DialogStep>('analysis')
  const [orderAnalysis, setOrderAnalysis] = useState<OrderAnalysis | null>(null)
  const toastRef = React.useRef<Toast>(null)
  
  // Use the real API hook
  const bulkReassignment = useBulkRejectedItemsReassignment(
    orderData.refresh,
    orderSelection.clearSelection
  )

  // Analyze selected orders when dialog opens
  React.useEffect(() => {
    if (visible && orderSelection.getSelectedOrdersCount() > 0) {
      analyzeSelectedOrders()
    }
  }, [visible, orderSelection])

  const analyzeSelectedOrders = () => {
    const selectedOrders = orderSelection.getSelectedOrdersData()
    
    // TODO: Replace with actual analysis logic
    // For now, using placeholder logic similar to the original
    const analysis: OrderAnalysis = {
      storeDeclinedOnly: Math.floor(selectedOrders.length * 0.5),
      adminRejectedOnly: Math.floor(selectedOrders.length * 0.3),
      mixedOrders: Math.floor(selectedOrders.length * 0.2),
      totalOrders: selectedOrders.length
    }
    
    setOrderAnalysis(analysis)
  }

  const handleProceedToConfirmation = () => {
    setCurrentStep('confirmation')
  }

  const handleExecuteReassignment = async () => {
    setCurrentStep('results')
    
    try {
      // Get selected orders and execute reassignment
      const selectedOrders = orderSelection.getSelectedOrdersData()
      await bulkReassignment.executeReassignment(selectedOrders)
      
      // Toast notifications are handled by the hook
    } catch (error) {
      console.error('Error in handleExecuteReassignment:', error)
      // Error handling is managed by the hook
    }
  }

  const resetDialog = () => {
    setCurrentStep('analysis')
    setOrderAnalysis(null)
    bulkReassignment.resetState()
  }

  const handleClose = () => {
    resetDialog()
    onCloseDialog()
  }

  const getDialogHeader = () => {
    switch (currentStep) {
      case 'analysis':
        return (
          <div className="flex align-items-center gap-2">
            <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.25rem', marginRight: '8px' }}></i>
            <span>Bulk Reassign Rejected Items</span>
          </div>
        )
      case 'confirmation':
        return (
          <div className="flex align-items-center gap-2">
            <i className="pi pi-question-circle" style={{ fontSize: '1.25rem', marginRight: '8px' }}></i>
            <span>Confirm Bulk Reassignment</span>
          </div>
        )
      case 'results':
        return (
          <div className="flex align-items-center gap-2">
            <i className={`pi ${bulkReassignment.processingState === 'processing' ? 'pi-spin pi-spinner' : bulkReassignment.processingState === 'completed' ? 'pi-check-circle' : 'pi-times-circle'}`} style={{ fontSize: '1.25rem', marginRight: '8px' }}></i>
            <span>
              {bulkReassignment.processingState === 'processing' ? 'Processing Reassignment...' : 
               bulkReassignment.processingState === 'completed' ? 'Reassignment Complete' : 
               'Reassignment Failed'}
            </span>
          </div>
        )
      default:
        return 'Bulk Reassign Rejected Items'
    }
  }

  const renderContent = () => {
    switch (currentStep) {
      case 'analysis':
        return (
          <RejectedItemsAnalysisView
            orderData={orderData}
            orderSelection={orderSelection}
            lineItemsHook={lineItemsHook}
            lineItemSelection={lineItemSelection}
            orderAnalysis={orderAnalysis}
          />
        )
      case 'confirmation':
        return (
          <ReassignmentConfirmationView
            orderData={orderData}
            orderSelection={orderSelection}
            lineItemsHook={lineItemsHook}
            lineItemSelection={lineItemSelection}
            orderAnalysis={orderAnalysis}
          />
        )
      case 'results':
        return (
          <RejectedItemsResultsView
            orderData={orderData}
            orderSelection={orderSelection}
            lineItemsHook={lineItemsHook}
            lineItemSelection={lineItemSelection}
            processingState={bulkReassignment.processingState}
            apiResponse={bulkReassignment.reassignmentResults}
          />
        )
      default:
        return null
    }
  }

  const renderFooter = () => {
    const isAnalysisStep = currentStep === 'analysis'
    const isConfirmationStep = currentStep === 'confirmation'
    const isResultsStep = currentStep === 'results'

    return (
      <div className="flex justify-content-end">
        <div className="flex">
          {isAnalysisStep && (
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={handleClose}
              className="p-button-warning"
            />
          )}
          
          {isAnalysisStep && orderAnalysis && (
            <Button
              label={`Review Reassignment Plan (${orderAnalysis.totalOrders} orders)`}
              icon="pi pi-arrow-right"
              onClick={handleProceedToConfirmation}
              className="p-button-primary ml-3"
            />
          )}
          
          {isConfirmationStep && (
            <Button
              label="Back"
              icon="pi pi-arrow-left"
              onClick={() => setCurrentStep('analysis')}
              className="p-button-danger"
            />
          )}
          
          {isConfirmationStep && (
            <Button
              label="Execute Reassignment"
              icon="pi pi-play"
              onClick={handleExecuteReassignment}
              className="p-button-success ml-3"
            />
          )}
          
          {isResultsStep && bulkReassignment.processingState !== 'processing' && (
            <Button
              label="Close"
              icon="pi pi-check"
              onClick={handleClose}
              className="p-button-primary"
            />
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <Toast ref={toastRef} />
      <Dialog
        header={getDialogHeader()}
        visible={visible}
        onHide={bulkReassignment.processingState !== 'processing' ? handleClose : () => {}}
        style={{ width: '800px' }}
        modal
        data-testid="bulk-rejected-items-dialog"
        footer={renderFooter()}
        closable={bulkReassignment.processingState !== 'processing'}
      >
        <div className="p-4">
          {renderContent()}
        </div>
      </Dialog>
    </>
  )
} 